import React from 'react';
import Downshift, { ControllerStateAndHelpers } from 'downshift';
import styled from 'styled-components';
import ICONS from '../constants/icons';
import { COLORS } from '../constants/styles';

import Icon from './Icon';
import { TickIcon } from './icons/TickIcon';
import { TextHelpers } from '../../../utils/textHelpers';

const DropdownWrapper = styled.div`
    position: relative;
`;

const DropdownButton = styled.button`
    border: none;
    background-color: transparent;
    color: currentColor;
    padding: 0;
    font-weight: 100;
    width: 100%;
    display: flex;
    justify-content: space-between;
    cursor: pointer;
`;

const List = styled.ul`
    list-style: none;
    padding: 0;
    margin: 5px 0;
    position: absolute;
    background-color: ${COLORS.white};
    z-index: 10;
    box-shadow: 0 13px 20px 0 rgba(0, 0, 0, 0.4);
    border-radius: 5px;
    min-width: 150px;
    width: 100%;
    max-height: 250px;
    overflow: auto;
    ${(props: IListPositionProps) => (props.listPosition === 'onTop' ? 'bottom: 16px' : '')};
`;

const Item = styled.button`
    display: flex;
    cursor: pointer;
    padding: 10px;
    text-align: left;
    border-radius: 5px;
    border-color: transparent;
    background: none;
    width: 100%;

    &:hover {
        background-color: ${COLORS.ultralightGrey};
    }

    ${(props: IItemPositionProps) => (props.selected === true ? 'font-weight: bold' : '')};
`;

const ItemSearchValue = styled.div`
    flex: 1;
`;

const ArrowIcon = styled.span`
    display: inline-block;
    margin-left: 5px;
`;

const TickIconWrapper = styled.span`
    display: inline-block;
    margin-left: 10px;
    margin-right: 6px;
`;

type IItemPositionProps = {
    selected?: boolean;
};
type IListPositionProps = {
    listPosition?: string;
};

type IDropdownProps = {
    items: string[];
    defaultSelected: string;
    onChange: (
        selectedItem: string,
        stateAndHelpers: ControllerStateAndHelpers<Record<string, any>>,
    ) => void;
    qaId: string;
    localeSet?: string;
    disabled?: boolean;
} & IItemPositionProps &
    IListPositionProps;

const Dropdown = ({
    items,
    defaultSelected,
    onChange,
    listPosition,
    qaId,
    localeSet,
    disabled = false,
    ...rest
}: IDropdownProps) => (
    <Downshift {...rest} onChange={onChange}>
        {({ getRootProps, getItemProps, isOpen, toggleMenu, selectedItem }) => {
            selectedItem = selectedItem ?? defaultSelected;
            return (
                <DropdownWrapper {...getRootProps({ refKey: 'innerRef' })}>
                    <DropdownButton
                        id="my-select"
                        type="button"
                        onClick={
                            toggleMenu as React.MouseEventHandler<HTMLButtonElement> | undefined
                        }
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded={isOpen}
                        data-qa-id={`${qaId}-dropdown-button`}
                        disabled={disabled}
                        tabIndex={0}
                    >
                        <div>{selectedItem}</div>

                        <ArrowIcon>
                            <Icon icon={ICONS.ARROW_DOWN} size="20" viewBoxMax="20" />
                        </ArrowIcon>
                    </DropdownButton>
                    {isOpen ? (
                        <List listPosition={listPosition} data-qa-id={`${qaId}-dropdown-list`}>
                            {items.map((item, index) => (
                                <li>
                                    <Item
                                        {...getItemProps({ item })}
                                        key={item}
                                        data-qa-id={`${qaId}-dropdown-item-${index}`}
                                        data-qa-value={item}
                                        selected={item === selectedItem}
                                    >
                                        <ItemSearchValue>{item}</ItemSearchValue>
                                        {item === selectedItem ? (
                                            <TickIconWrapper>
                                                {' '}
                                                <TickIcon />
                                            </TickIconWrapper>
                                        ) : null}
                                    </Item>
                                </li>
                            ))}
                        </List>
                    ) : null}
                </DropdownWrapper>
            );
        }}
    </Downshift>
);

export default Dropdown;
